import type { ShiftOutcome, ShiftOutcomeKind } from '../../partner/index.js'

/**
 * A worker's outcomes grouped by the month of the shift - the period a
 * score is reckoned over - so a long record reads as one line per month
 * before any of it is opened. Groups keep the order the outcomes arrived
 * in (most recent first from the API), as do the outcomes within each.
 */
export interface OutcomeGroup {
  /** `YYYY-MM` of the shifts' dates. */
  month: string
  outcomes: readonly ShiftOutcome[]
  counts: Readonly<Record<ShiftOutcomeKind, number>>
  clinicalBridge: number
}

export const OUTCOME_KINDS: readonly ShiftOutcomeKind[] = ['completed', 'callout', 'no_show']

const NO_COUNTS: Readonly<Record<ShiftOutcomeKind, number>> = { completed: 0, callout: 0, no_show: 0 }

function monthOf(outcome: ShiftOutcome): string {
  return outcome.shiftDate.slice(0, 7)
}

function addToGroup(group: OutcomeGroup, outcome: ShiftOutcome): OutcomeGroup {
  return {
    ...group,
    outcomes: [...group.outcomes, outcome],
    counts: { ...group.counts, [outcome.kind]: group.counts[outcome.kind] + 1 },
    clinicalBridge: group.clinicalBridge + (outcome.clinicalBridge ? 1 : 0),
  }
}

export function groupOutcomesByMonth(outcomes: readonly ShiftOutcome[]): OutcomeGroup[] {
  return outcomes.reduce<OutcomeGroup[]>((groups, outcome) => {
    const month = monthOf(outcome)
    const index = groups.findIndex((group) => group.month === month)
    if (index === -1) {
      return [...groups, addToGroup({ month, outcomes: [], counts: NO_COUNTS, clinicalBridge: 0 }, outcome)]
    }
    return groups.map((group, position) => (position === index ? addToGroup(group, outcome) : group))
  }, [])
}

/** "September 2026" for `2026-09`; the label falls back to the raw value for anything that is not a month. */
export function formatMonth(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month)
  if (!match) {
    return month
  }
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** "Oct 2026": the month short, for a grid that fits four to a line. */
export function formatMonthShort(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month)
  if (!match) {
    return month
  }
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}
